// /share-bundles wire schemas — the dashboard-side (authenticated) half
// of the postventa share-link feature. A supervisor selects one or more
// postventa observations and mints a bundle: one opaque URL that
// resolves, server-side, to N independently valid/revocable share_links
// rows. See apps/core-api/src/db/schema.ts (shareBundles,
// shareBundleItems) for the data model and ADR-0069 for the
// full design.
//
// Recipient identity is ad-hoc — no provider record is required to mint
// a bundle. Prefer recipientProviderContactId when the recipient is
// already in the org's provider directory; recipientLabel is a non-PII
// fallback (business/trade name) for genuinely ad-hoc recipients.
import { z } from "zod";
// council-settled cap: 20 findings per bundle at mint time (env-tunable
// server-side via SHARE_BUNDLE_MAX_TOKENS), 50 as an unconditional read-
// path ceiling. This schema enforces the mint-time number.
export const SHARE_BUNDLE_MAX_FINDINGS = 20;
// Plain z.string().uuid() rather than the branded findingIdSchema — this
// is a client-supplied wire body, not an internal engine entity; the
// server independently confirms existence + org ownership via a DB
// lookup, so branding buys nothing extra here and only adds friction to
// FE code that consumes plain-string finding ids from other endpoints.
export const shareBundleCreateBody = z
    .object({
    findingIds: z.array(z.string().uuid()).min(1).max(SHARE_BUNDLE_MAX_FINDINGS),
    recipientProviderContactId: z.string().uuid().optional(),
    recipientLabel: z.string().trim().min(1).max(120).optional(),
    recipientContactAdhoc: z.string().trim().min(1).max(160).optional(),
    expiresAt: z.string().datetime().optional(),
})
    .strict();
export const shareBundleCreateResponse = z.object({
    id: z.string().uuid(),
    // Raw bundle token, present ONLY in the create response — never
    // stored, never returned again.
    token: z.string(),
    // Full share URL (server-composed — the dashboard never builds this
    // itself). Path shape `/r/b/<token>` lands with the S2 public-read
    // slice; until then this is dead-weight for the FE to display/copy,
    // which is still useful before the reader route exists.
    url: z.string().url(),
    recipientLabel: z.string().nullable(),
    recipientContactAdhoc: z.string().nullable(),
    itemCount: z.number().int().positive(),
    expiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const shareBundleListItemSchema = z.object({
    id: z.string().uuid(),
    recipientLabel: z.string().nullable(),
    recipientContactAdhoc: z.string().nullable(),
    itemCount: z.number().int().nonnegative(),
    // Rows in share_link_accesses across every link of the bundle — the
    // «Accesos» column of Administración · Enlaces compartidos (autogestión
    // §2.5). The per-access detail stays behind GET /share-bundles/:id/accesses.
    accessCount: z.number().int().nonnegative(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const shareBundleListResponse = z.object({
    bundles: z.array(shareBundleListItemSchema),
});
export const shareBundleParams = z.object({ id: z.string().uuid() });
// GET /share-bundles/:id/accesses (S4) — "quién abrió este link, cuándo,
// y qué hizo" per ADR-0020 Sprint D's original share_link_accesses
// intent. Rows come from every share_links row under this bundle, not
// just the bundle token itself — a per-item view request also counts.
export const shareBundleAccessSchema = z.object({
    accessedAt: z.string().datetime(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    action: z.string().nullable(),
});
// ADR-0073 — minimal supervisor-side view of what a provider actually
// submitted through one of this bundle's share links. One entry per
// share link that has a provider submission; findingElement/
// findingAreaName is included only so the dashboard can label which
// observation a photo/note belongs to when a bundle covers more than
// one — same shape as `publicBundleProofSchema` (the provider's own
// view) plus that one label field, deliberately kept in lockstep.
export const shareBundleProofEntrySchema = z.object({
    shareLinkId: z.string().uuid(),
    findingElement: z.string().nullable(),
    findingAreaName: z.string().nullable(),
    photos: z.array(z.object({ url: z.string().url(), submittedAt: z.string().datetime() })),
    note: z.string().nullable(),
    submittedAt: z.string().datetime().nullable(),
});
export const shareBundleAccessesResponse = z.object({
    accesses: z.array(shareBundleAccessSchema),
    proof: z.array(shareBundleProofEntrySchema),
});
//# sourceMappingURL=share-bundles.js.map